
import { authHeaders, jsonAuthHeaders, normalizeApiBaseUrl } from "./api";

const BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_BASE_URL);

if (!BASE_URL) {
	throw new Error("REACT_APP_BASE_URL is not defined in environment variables");
}

export async function loginUser(email, password) {
	try {
		const response = await fetch(`${BASE_URL}/login`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email, password }),
		});

		if (!response.ok) {
			console.log("Invalid credentials.");
			const errorData = await response.json();
			throw new Error(errorData.detail || "Login failed");
		}

		const data = await response.json();
		return data; // { message: "Login successful", id: "..." }
	} catch (error) {
		throw new Error(error.message || "Login error");
	}
}


// New function for combined signup with onboarding data
export async function signupUserWithOnboarding(userData, onboardingAnswers) {
	try {
		// Transform onboarding answers to user schema fields
		const transformedOnboardingData = transformOnboardingAnswers(onboardingAnswers);
		
		// Combine user data with onboarding data
		const completeUserData = {
			...userData,
			...transformedOnboardingData
		};

		console.log("Complete user data for signup:", completeUserData);

		const response = await fetch(`${BASE_URL}/users`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(completeUserData),
		});

		if (!response.ok) {
			const error = await response.json();
			console.log("Signup error:", error);
			throw new Error(error.detail || "Signup failed");
		}

		return await response.json();
	} catch (error) {
		throw error;
	}
}

export async function updateUser(userId, userData) {
	try {
		const response = await fetch(`${BASE_URL}/users/${userId}`, {
			method: "PUT",
			headers: jsonAuthHeaders(),
			body: JSON.stringify(userData),
		});

		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.detail || "Update failed");
		}

		return await response.json();
	} catch (error) {
		throw error;
	}
}

// Helper function to transform onboarding answers to user schema format
export const transformOnboardingAnswers = (onboardingAnswers) => {
	const userData = {};
	
	console.log("Transforming onboarding answers:", onboardingAnswers);
	
	// Map question IDs to user fields based on the actual onboarding questions
	// This should be updated based on your actual question structure
	Object.entries(onboardingAnswers).forEach(([questionId, answer]) => {
		console.log(`Processing question ${questionId} with answer:`, answer);
		
		// Handle different answer types
		if (typeof answer === 'string') {
			// For string answers, we need to know which question this is
			// For now, let's use a more reliable approach
			if (answer.toLowerCase().includes('experience') || answer.toLowerCase().includes('beginner') || answer.toLowerCase().includes('intermediate') || answer.toLowerCase().includes('expert')) {
				userData.experienceLevel = answer;
			} else if (answer.toLowerCase().includes('confident')) {
				// Handle confidence levels
				const confidenceMap = {
					'not confident at all': 1,
					'slightly confident': 2,
					'somewhat confident': 3,
					'confident': 4,
					'very confident': 5
				};
				userData.confidence = confidenceMap[answer.toLowerCase()] || 3;
			} else if (answer.toLowerCase().includes('describe') || answer.toLowerCase().includes('about')) {
				userData.describe = answer;
			} else if (answer.toLowerCase().includes('tools') || answer.toLowerCase().includes('equipment')) {
				userData.tools = answer;
			} else if (answer.toLowerCase().includes('projects') || answer.toLowerCase().includes('interested')) {
				userData.interestedProjects = answer;
			} else {
				// Default to describe if it's a long text answer
				if (!userData.describe && answer.length > 20) {
					userData.describe = answer;
				}
			}
		} else if (Array.isArray(answer)) {
			// For array answers (multiple selection), join them
			userData.tools = answer.join(', ');
		} else if (typeof answer === 'object' && answer.country) {
			// For location objects
			userData.country = answer.country;
			userData.state = answer.state;
		} else if (typeof answer === 'number') {
			// For numeric answers (confidence levels)
			userData.confidence = answer;
		}
	});
	
	console.log("Transformed user data:", userData);
	return userData;
};

// Helper function to handle string answers
export const handleStringAnswer = (answer, userData) => {
	const lowerAnswer = answer.toLowerCase();
	
	if (lowerAnswer.includes('experience') || lowerAnswer.includes('level')) {
		userData.experienceLevel = answer;
	} else if (lowerAnswer.includes('confidence') || lowerAnswer.includes('confident')) {
		const confidenceMap = {
			'not confident at all': 1,
			'slightly confident': 2,
			'somewhat confident': 3,
			'confident': 4,
			'very confident': 5
		};
		userData.confidence = confidenceMap[lowerAnswer] || 3;
	} else if (lowerAnswer.includes('describe') || lowerAnswer.includes('about')) {
		userData.describe = answer;
	} else if (lowerAnswer.includes('tools') || lowerAnswer.includes('equipment')) {
		userData.tools = answer;
	} else if (lowerAnswer.includes('projects') || lowerAnswer.includes('interested')) {
		userData.interestedProjects = answer;
	} else if (lowerAnswer.includes('country')) {
		userData.country = answer;
	} else if (lowerAnswer.includes('state') || lowerAnswer.includes('province')) {
		userData.state = answer;
	} else {
		// Default case - try to infer the field based on the answer content
		if (!userData.describe && (lowerAnswer.length > 20 || lowerAnswer.includes(' '))) {
			userData.describe = answer;
		} else if (!userData.tools && (lowerAnswer.includes('tool') || lowerAnswer.includes('equipment'))) {
			userData.tools = answer;
		} else if (!userData.interestedProjects && (lowerAnswer.includes('project') || lowerAnswer.includes('interested'))) {
			userData.interestedProjects = answer;
		}
	}
};

export async function getUserById(userId) {
  const res = await fetch(`${BASE_URL}/users/${userId}`, {
	headers: authHeaders(),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { msg = (await res.json()).detail || msg; } catch {}
    throw new Error(msg);
  }
  return res.json(); // { _id, firstname, lastname, ... }
}

export async function syncCognitoUser(idToken) {
	if (!idToken) {
		throw new Error("Missing Cognito ID token");
	}

	const res = await fetch(`${BASE_URL}/auth/me`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${idToken}`,
		},
	});

	if (!res.ok) {
		let msg = res.statusText;
		try {
			msg = (await res.json()).detail || msg;
		} catch {}
		throw new Error(msg);
	}

	return res.json();
}

// Check if email already exists in database
export async function checkEmailExists(email) {
	// Since backend doesn't have a dedicated email check endpoint,
	// we'll let the backend handle email existence during actual signup
	// This prevents the complex create/delete logic that can cause issues
	return false;
}

// Create user during signup (before onboarding)
export async function createUserDuringSignup(userData) {
	try {
		const response = await fetch(`${BASE_URL}/users`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(userData),
		});

		if (!response.ok) {
			const error = await response.json();
			console.log("Signup error:", error);
			throw new Error(error.detail || "Signup failed");
		}

		return await response.json();
	} catch (error) {
		console.log("Signup error:", error);
		throw error;
	}
}

// Check if user has completed onboarding
export function hasCompletedOnboarding(user) {
	// Check if user has any onboarding fields
	// Since onboarding questions are optional, we just need to check if the user
	// has gone through the onboarding process and has at least some data
	const onboardingFields = [
		'experienceLevel',
		'confidence', 
		'tools',
		'interestedProjects',
		'country',
		'state',
		'describe'
	];
	
	// Check if user has any onboarding data
	const hasAnyOnboardingData = onboardingFields.some(field => {
		const value = user[field];
		return value && 
			   value !== "" && 
			   value !== null && 
			   value !== undefined &&
			   (typeof value === 'string' ? value.trim().length > 0 : true);
	});
	
	// Alternative: Check if user has been through onboarding by looking for any onboarding-related field
	// This is more lenient and accounts for optional questions
	const hasOnboardingExperience = hasAnyOnboardingData || 
		(user.experienceLevel || user.confidence || user.tools || user.interestedProjects || 
		 user.country || user.state || user.describe);
	
	// User has completed onboarding if they have any onboarding data
	// This is more flexible and accounts for optional questions
	return hasOnboardingExperience;
}
